import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { QRCodeSVG } from 'qrcode.react';

export default function QRCode() {
  const [restaurant, setRestaurant] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [scanStats, setScanStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [scanPeriod, setScanPeriod] = useState('total');
  const toast = useToast();

  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setRestaurant(data[0]);
        const rid = data[0].restaurant_id;
        Promise.all([
          api.get(`/restaurants/${rid}/qr-codes`),
          api.get(`/restaurants/${rid}/scan-stats`).catch(() => null),
        ]).then(([qrs, stats]) => {
          setQrCodes(qrs);
          setScanStats(stats);
        }).catch(err => toast.error(err.message))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, []);

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = (qr) => {
    const svg = document.getElementById(`qr-${qr.qr_id}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 400;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, 400, 400);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 400, 400);
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `qr-menu-${restaurant.slug}.png`;
      link.click();
      toast.success('QR code downloaded!');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const printQR = (qr) => {
    const svg = document.getElementById(`qr-${qr.qr_id}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>QR Code - ${restaurant.name}</title>
      <style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:sans-serif;}
      .container{text-align:center;padding:40px;}h1{font-size:24px;margin-bottom:8px;}p{color:#666;margin-bottom:24px;}
      svg{width:280px;height:280px;}.url{margin-top:16px;font-size:12px;color:#999;word-break:break-all;max-width:300px;}</style>
      </head><body><div class="container"><h1>${restaurant.name}</h1><p>Scan to view our menu</p>${svgData}<p class="url">${qr.public_url}</p></div></body></html>
    `);
    win.document.close();
    win.print();
  };

  const mainQR = qrCodes[0];
  const menuUrl = restaurant ? `${window.location.origin}/menu/${restaurant.slug}` : '';

  if (loading) return <AdminLayout title="QR Codes"><div className="loading-center"><div className="loading-spinner"></div></div></AdminLayout>;

  if (!restaurant) return (
    <AdminLayout title="QR Codes">
      <div className="empty-state">
        <div className="empty-state-icon">📱</div>
        <h3>No restaurant found</h3>
        <p>Please create your restaurant in Settings first</p>
        <a href="/admin/settings" className="btn btn-primary mt-4">Go to Settings</a>
      </div>
    </AdminLayout>
  );

  const totalScans = mainQR?.scan_count || 0;
  const todayScans = scanStats?.today || 0;
  const weekScans = scanStats?.this_week || 0;
  const monthScans = scanStats?.this_month || 0;
  const periodScans = scanPeriod === 'total' ? totalScans : scanPeriod === 'today' ? todayScans : scanPeriod === 'week' ? weekScans : monthScans;
  const periodLabel = scanPeriod === 'total' ? 'All Time' : scanPeriod === 'today' ? 'Today' : scanPeriod === 'week' ? 'This Week' : 'This Month';

  return (
    <AdminLayout title="QR Codes">
      <div style={{ maxWidth: 700 }}>
        <div className="card mb-6">
          <div className="card-header">
            <h2 className="card-title">📱 Restaurant QR Code</h2>
          </div>
          <div className="card-body">
            <div className="flex gap-8 flex-wrap">
              <div className="text-center">
                <div className="qr-container">
                  <QRCodeSVG
                    id={mainQR ? `qr-${mainQR.qr_id}` : 'qr-preview'}
                    value={menuUrl}
                    size={200}
                    level="H"
                    fgColor="#000000"
                    bgColor="#FFFFFF"
                  />
                </div>
                <p className="text-muted text-sm mt-3">Scan to open menu</p>
              </div>
              <div style={{ flex: 1, minWidth: 240 }} className="space-y-4">
                <div>
                  <label className="form-label mb-2">Public Menu URL</label>
                  <div className="flex gap-2">
                    <div className="qr-url-box" style={{ flex: 1 }}>{menuUrl}</div>
                    <button className="btn btn-outline btn-icon" onClick={() => copyUrl(menuUrl)}>
                      {copied ? '✅' : '📋'}
                    </button>
                    <a href={menuUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-icon">↗️</a>
                  </div>
                </div>

                {/* Scan Stats */}
                <div>
                  <label className="form-label mb-2">📊 Scan Analytics</label>
                  <div style={{ background: 'var(--primary-light)', borderRadius: 12, padding: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 36, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>{periodScans}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, marginBottom: 14 }}>{periodLabel}</div>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      {[
                        { key: 'total', label: 'All Time' },
                        { key: 'today', label: 'Today' },
                        { key: 'week', label: 'This Week' },
                        { key: 'month', label: 'This Month' },
                      ].map(p => (
                        <button
                          key={p.key}
                          onClick={() => setScanPeriod(p.key)}
                          style={{
                            padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                            cursor: 'pointer', border: 'none',
                            background: scanPeriod === p.key ? 'var(--primary)' : 'white',
                            color: scanPeriod === p.key ? 'white' : 'var(--text-muted)',
                            transition: 'all 0.15s',
                          }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="form-label mb-2">Download QR Code</p>
                  <div className="flex gap-2 flex-wrap">
                    <button className="btn btn-outline btn-sm" onClick={() => mainQR && downloadQR(mainQR)}>⬇️ Download PNG</button>
                    <button className="btn btn-outline btn-sm" onClick={() => mainQR && printQR(mainQR)}>🖨️ Print</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ background: 'var(--primary-light)' }}>
          <div className="card-body">
            <h3 className="font-semibold mb-3">💡 QR Code Tips</h3>
            <ul className="space-y-2 text-sm text-muted">
              <li>• Print at minimum 2cm × 2cm for reliable scanning</li>
              <li>• Place at table level or near entrance</li>
              <li>• Test on multiple phones before printing in bulk</li>
              <li>• Add "Scan for Menu" label near the QR code</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}